/**
 * MotionContext - Layer 3: Motion Integration
 *
 * Bridges MolecularWorkspace atom positions to Motion's MotionValues.
 * Enables 60fps animations without React re-renders.
 *
 * Architecture:
 * - Creates MotionValue<x>, MotionValue<y> for each atom
 * - Syncs with workspace atom positions
 * - Provides imperative access via getAtomMotion(atomId)
 * - Updates workspace via onWorkspaceChange when motion changes
 *
 * Usage:
 * ```tsx
 * <MolecularSVGProvider workspace={workspace}>
 *   <MotionProvider>
 *     <Atom id="atom1" />  // Uses motion.g with MotionValues
 *   </MotionProvider>
 * </MolecularSVGProvider>
 * ```
 */

import React, { createContext, useContext, useMemo, useRef, useEffect, type ReactNode } from 'react';
import { motionValue, animate, type MotionValue } from 'motion/react';
import { useMolecularSVG } from './MolecularSVGContext';

/**
 * Motion values for a single atom
 */
export interface AtomMotionValues {
  /** X-position MotionValue (in SVG coordinates) */
  x: MotionValue<number>;
  /** Y-position MotionValue (in SVG coordinates) */
  y: MotionValue<number>;
}

/**
 * Motion context value providing access to atom MotionValues
 */
export interface MotionContextValue {
  /**
   * Get MotionValues for a specific atom
   * @param atomId - Atom identifier from workspace
   * @returns MotionValues for x and y position, or undefined if atom not found
   */
  getAtomMotion: (atomId: string) => AtomMotionValues | undefined;

  /**
   * Get all atom IDs that have motion values
   */
  getAtomIds: () => string[];

  /**
   * Ref indicating whether a drag is in progress.
   * Used to suppress render-phase spring sync that would fight the drag.
   */
  isDraggingRef: React.MutableRefObject<boolean>;
  /** Set to true to skip spring animations during bulk workspace updates (PIPH-63) */
  suppressAnimationRef: React.MutableRefObject<boolean>;
}

const MotionContext = createContext<MotionContextValue | null>(null);

export interface MotionProviderProps {
  /**
   * Child components that need access to motion values
   */
  children: ReactNode;

  /**
   * Optional callback when atom positions change via motion
   * Use this to sync motion changes back to workspace
   */
  onPositionChange?: (atomId: string, x: number, y: number) => void;
}

/**
 * MotionProvider - Creates and manages MotionValues for molecular animations
 *
 * This provider:
 * 1. Reads atom positions from MolecularSVGContext
 * 2. Creates MotionValue<x>, MotionValue<y> for each atom
 * 3. Initializes values from workspace coordinates (converted to SVG y-down)
 * 4. Provides accessor methods for components to consume motion values
 *
 * @example
 * ```tsx
 * <MolecularSVGProvider workspace={workspace}>
 *   <MotionProvider onPositionChange={(id, x, y) => console.log('Atom moved', id, x, y)}>
 *     <SVGCanvas />
 *   </MotionProvider>
 * </MolecularSVGProvider>
 * ```
 */
export function MotionProvider({ children, onPositionChange }: MotionProviderProps) {
  const { workspace } = useMolecularSVG();

  // Store motion values in a ref to persist across renders
  // Map: atomId -> { x: MotionValue, y: MotionValue }
  const motionValuesRef = useRef<Map<string, AtomMotionValues>>(new Map());

  // Flag to suppress render-phase spring sync during drags (MC-851)
  const isDraggingRef = useRef(false);
  // Flag to suppress spring animations during bulk workspace updates (PIPH-63)
  const suppressAnimationRef = useRef(false);

  // Sync motion values with workspace DURING RENDER (before children render)
  // This ensures MotionValues exist when DynamicAtom components render
  const currentAtomIds = new Set<string>();

  // Helper: create or sync a single atom's MotionValues
  const syncAtom = (atomId: string, x: number, y: number) => {
    currentAtomIds.add(atomId);
    const expectedY = -y; // Y-FLIP: chemistry y-up → SVG y-down

    if (!motionValuesRef.current.has(atomId)) {
      motionValuesRef.current.set(atomId, {
        x: motionValue(x),
        y: motionValue(expectedY),
      });
    } else {
      const mv = motionValuesRef.current.get(atomId)!;
      if (mv.x.get() !== x || mv.y.get() !== expectedY) {
        if (!isDraggingRef.current) {
          if (suppressAnimationRef.current) {
            mv.x.set(x);
            mv.y.set(expectedY);
          } else {
            animate(mv.x, x, { type: 'spring', stiffness: 120, damping: 20, mass: 1 });
            animate(mv.y, expectedY, { type: 'spring', stiffness: 120, damping: 20, mass: 1 });
          }
        }
      }
    }
  };

  // Create or update motion values for each loose atom
  for (const atom of workspace.getAllAtoms()) {
    syncAtom(atom.id, atom.x, atom.y);
  }

  // Also create MotionValues for collapsed instance atoms (compound IDs).
  // This allows connective bonds (bonds between instances and loose atoms)
  // to render through DynamicBond with real-time motion tracking.
  for (const instance of workspace.getGroupInstances()) {
    const template = workspace.getTemplate(instance.templateId);
    if (!template) continue;
    const { tx, ty, rotation } = instance.transform;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    for (const localAtom of template.workspace.getAllAtoms()) {
      const compoundId = `${instance.id}:${localAtom.id}`;
      const worldX = localAtom.x * cos - localAtom.y * sin + tx;
      const worldY = localAtom.x * sin + localAtom.y * cos + ty;
      syncAtom(compoundId, worldX, worldY);
    }
  }

  // Remove motion values for atoms that no longer exist in workspace
  for (const atomId of motionValuesRef.current.keys()) {
    if (!currentAtomIds.has(atomId)) {
      motionValuesRef.current.delete(atomId);
    }
  }

  // Set up change listeners in useEffect (only for side effects/cleanup)
  useEffect(() => {
    if (onPositionChange) {
      // Clear all existing listeners first
      motionValuesRef.current.forEach((motionValues) => {
        motionValues.x.clearListeners();
        motionValues.y.clearListeners();
      });

      // Add new listeners
      motionValuesRef.current.forEach((motionValues, atomId) => {
        const onChange = () => {
          const x = motionValues.x.get();
          const y = motionValues.y.get();
          onPositionChange(atomId, x, -y); // Y-FLIP back to chemistry coordinates
        };

        motionValues.x.on('change', onChange);
        motionValues.y.on('change', onChange);
      });
    }

    // Cleanup listeners on unmount
    return () => {
      motionValuesRef.current.forEach((motionValues) => {
        motionValues.x.clearListeners();
        motionValues.y.clearListeners();
      });
    };
  }, [onPositionChange]);

  // Create stable context value using useMemo
  const contextValue = useMemo<MotionContextValue>(
    () => ({
      getAtomMotion: (atomId: string) => motionValuesRef.current.get(atomId),
      getAtomIds: () => Array.from(motionValuesRef.current.keys()),
      isDraggingRef,
      suppressAnimationRef,
    }),
    [] // Empty deps - refs are stable
  );

  return <MotionContext.Provider value={contextValue}>{children}</MotionContext.Provider>;
}

/**
 * Hook to access motion context
 *
 * @returns MotionContextValue with atom motion accessors
 * @throws Error if used outside MotionProvider
 */
export function useMotion(): MotionContextValue {
  const context = useContext(MotionContext);
  if (!context) {
    throw new Error('useMotion must be used within a MotionProvider');
  }
  return context;
}

/**
 * Hook to get MotionValues for a specific atom
 *
 * Convenience hook that combines useMotion() + getAtomMotion()
 *
 * @param atomId - Atom identifier
 * @returns AtomMotionValues or undefined if atom not found
 *
 * @example
 * ```tsx
 * function AtomComponent({ id }: { id: string }) {
 *   const motion = useAtomMotion(id);
 *   if (!motion) return null;
 *
 *   return (
 *     <motion.g style={{ x: motion.x, y: motion.y }}>
 *       <circle r={5} fill="black" />
 *     </motion.g>
 *   );
 * }
 * ```
 */
export function useAtomMotion(atomId: string): AtomMotionValues | undefined {
  const { getAtomMotion } = useMotion();
  return getAtomMotion(atomId);
}
